import React, { useMemo, useState, useCallback } from "react";
import Table from "../../../../../components/TableV2";
import TitleButtons from "../../../../../components/TitleButtons";
import { useNavigate } from "react-router-dom";
import { generatePdfReport } from "../../../../../utils/pdf/generatePdfReport";
import { useProdutosDisponiveis } from "./ProdutoData";
import TableActions from "../../../../../components/TableActions";

const ComercialProdutos: React.FC = () => {
  const navigate = useNavigate();
  const { produtos, loading } = useProdutosDisponiveis();

  // =======================
  // Helpers de moeda
  // =======================
  function resolveCurrencyCode(moeda?: string | null): string {
    if (!moeda) return "BRL";
    const m = moeda.trim().toUpperCase();

    if (m === "BRL" || m === "R$") return "BRL";
    if (m === "USD" || m === "US$" || m === "$") return "USD";
    if (m === "EUR" || m === "€") return "EUR";

    // fallback padrão
    return m || "BRL";
  }

  const formatCurrency = (v: unknown, moeda?: string | null) => {
    if (v === null || v === undefined || v === "") return "—";
    const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));
    if (!Number.isFinite(n)) return "—";

    const currency = resolveCurrencyCode(moeda);

    try {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency,
      }).format(n);
    } catch {
      // se der erro em moeda inválida, cai pra BRL
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(n);
    }
  };

  const formatPercent = (v: unknown) => {
    if (v === null || v === undefined || v === "") return "—";
    const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));
    if (!Number.isFinite(n)) return "—";
    return `${n.toFixed(2)}%`;
  };

  // =======================
  // Cabeçalhos e larguras
  // =======================

  const headers = useMemo(
    () => [
      "Código",
      "NCM",
      "Nome do Produto",
      "Quantidade",
      "Custo de Compra",
      "Margem",
      "Preço de Venda",
    ],
    []
  );

  const columnWidths = useMemo(
    () => ["100px", "60px", "270px", "80px", "110px", "80px", "120px"],
    []
  );

  // =======================
  // Linhas da tabela
  // =======================
  const rows = useMemo(
    () =>
      produtos.map((p) => [
        p.codigo,
        p.ncm,
        p.nome,
        p.quantidade ?? "—",
        formatCurrency(p.custo_compra ?? p.custo_unitario, p.moeda), // 👈 usa moeda
        formatPercent(p.margem_percentual),
        formatCurrency(p.preco_venda, p.moeda), // 👈 idem
      ]),
    [produtos]
  );

  // =======================
  // Estilo condicional: margem 0 -> linha vermelha pastel
  // =======================
  const getCellStyle = useCallback(
    (
      _rowIndex: number,
      _colIndex: number,
      _value: string | number | React.ReactNode,
      originalIndex: number
    ): React.CSSProperties | undefined => {
      const produto = produtos[originalIndex];
      if (!produto) return;

      if (produto.margem_percentual === 0) {
        return {
          backgroundColor: "#FF47474C",
        };
      }

      return;
    },
    [produtos]
  );

  // =======================
  // Seleção de colunas p/ PDF
  // =======================
  const [selectedColumns, setSelectedColumns] = useState<boolean[]>(
    headers.map(() => true)
  );

  const enabledColumnIndexes = useMemo(
    () =>
      selectedColumns
        .map((checked, i) => (checked ? i : -1))
        .filter((i) => i !== -1),
    [selectedColumns]
  );

  const firstCode = rows.length ? rows[0][0] : "—";
  const lastCode = rows.length ? rows[rows.length - 1][0] : "—";
  const nextCode = `PRD001${(produtos.length + 1).toString().padStart(2, "0")}`;

  return (
    <div>
      <TitleButtons fullBleedLine title="Comercial - Produtos" />

      <Table
        headers={headers}
        rows={rows}
        columnWidths={columnWidths}
        enableCellColors
        getCellStyle={getCellStyle}
        onDoubleClick={(_viewIndex, rowData) => {
          const codigoIdx = headers.indexOf("Código");
          const codigo = String(rowData[codigoIdx] ?? "");
          if (codigo) {
            navigate(`/comercial/produtos/${encodeURIComponent(codigo)}`);
          }
        }}
        responsiveWidths={{
          1300: {
            1: "100px",
            2: "60px",
            3: "260px",
            4: "80px",
            5: "110px",
            6: "80px",
            7: "120px",
          },
          1200: {
            1: "100px",
            2: "60px",
            3: "200px",
            4: "0px",
            5: "100px",
            6: "70px",
            7: "100px",
          },
        }}
      />

      {loading && (
        <div style={{ padding: 16, opacity: 0.7, fontSize: 14 }}>
          Carregando produtos…
        </div>
      )}
    </div>
  );
};

export default ComercialProdutos;
